// Report.tsx
import React, { useEffect, useRef, useState } from "react";
import { Post } from "../../models/post";
import { ChildProfile } from "../../models/childProfile";
import { groupPostsByDate } from "../../services/postService";
import { deriveBrandShades } from "../../utils/colourUtils";
import { ReportTemplateConfig, STOCK_WEEKLY_TEMPLATE } from "../../models/reportTemplate";
import { resolveHeaderFields, ResolvedHeaderField } from "../../services/reportFieldResolvers";
import { AttendanceStats } from "../../services/attendanceStatsService";
import AttendanceSection from "./AttendanceSection";
import "./reportView.scss";

export interface ReportProps {
    posts: Post[];
    childName?: string;
    reportDate?: string;
    includeHeader?: boolean;
    printWindow?: Window;
    /** Use thumbnail URLs instead of full-size URLs — keeps PDFs small */
    useThumbnails?: boolean;
    /** Organisation logo URL. Falls back to /logo_transparent.png if not provided. */
    logoUrl?: string | null;
    /** Organisation name for the "Setting" row of the header. Row is hidden if absent. */
    orgName?: string | null;
    /** Report author (PIN-active user who generated the report). Row hidden if absent. */
    authorName?: string | null;
    /** Organisation brand colour (hex). Themes the report; falls back to brand green. */
    brandColour?: string | null;
    /** Report Builder template driving the header. Defaults to the stock
     *  Weekly Report's minimal header — callers pass the real (fetched)
     *  template config. */
    template?: ReportTemplateConfig;
    /** Template name — feeds the derived "Report Type" field. */
    templateName?: string | null;
    /** Generation-panel values for manual-editable fields, by field key. */
    fieldValues?: Record<string, string>;
    /** Full profile when available — enables alias/initials/reference fields. */
    child?: ChildProfile | null;
    /** Period bounds (YYYY-MM-DD) — enable the derived "Report Period" field. */
    startDate?: string | null;
    endDate?: string | null;
    /** Phase 2 attendance stats for (child, period). Feeds the attendance
     *  section and the attendance-derived header fields. Null → those omit. */
    attendance?: AttendanceStats | null;
}

/** Chunk resolved fields into table rows: two field cells per row in
 *  two-column mode, with fullWidth fields taking a whole row (td spans). */
const toHeaderRows = (fields: ResolvedHeaderField[], columns: 1 | 2): ResolvedHeaderField[][] => {
    if (columns === 1) return fields.map(f => [f]);
    const rows: ResolvedHeaderField[][] = [];
    let pending: ResolvedHeaderField | null = null;
    for (const f of fields) {
        if (f.fullWidth) {
            if (pending) { rows.push([pending]); pending = null; }
            rows.push([f]);
        } else if (pending) {
            rows.push([pending, f]);
            pending = null;
        } else {
            pending = f;
        }
    }
    if (pending) rows.push([pending]);
    return rows;
};

const ReportView: React.FC<ReportProps> = (props) => {
    const {
        posts, childName, reportDate, includeHeader, printWindow, useThumbnails,
        logoUrl, orgName, authorName, brandColour,
        template = STOCK_WEEKLY_TEMPLATE.config, templateName, fieldValues,
        child, startDate, endDate, attendance,
    } = props;

    // Derive the report palette from the org's brand colour and expose it as CSS
    // variables on the report root, so the report document matches the org's
    // branding (reportView.scss reads var(--brand-*) with the green as fallback).
    const brandStyle = brandColour
        ? (() => {
            const s = deriveBrandShades(brandColour);
            return {
                '--brand-primary': s.primary,
                '--brand-dark': s.dark,
                '--brand-darker': s.darker,
                '--brand-light': s.light,
            } as React.CSSProperties;
        })()
        : undefined;

    // The template's posts section controls the report body: absent → no
    // posts rendered (future attendance-only templates), includeImages: false
    // → text-only posts.
    const postsSection = template.sections.find(
        (s): s is Extract<typeof s, { type: 'posts' }> => s.type === 'posts'
    );
    const attendanceSection = template.sections.find(
        (s): s is Extract<typeof s, { type: 'attendance' }> => s.type === 'attendance'
    );

    const orderedPosts = posts.slice();

    // Sort all posts chronologically (ascending)
    orderedPosts.sort(
        (a, b) => new Date(a.post_date).getTime() - new Date(b.post_date).getTime()
    );

    const groupedPosts = groupPostsByDate(orderedPosts);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const [imagesLoaded, setImagesLoaded] = useState<boolean>(false);

    const handlePrintClick = () => {
        printWindow?.focus();
        printWindow?.print();
    };

    // Wait for all images inside this component to finish loading (or error)
    useEffect(() => {

        setImagesLoaded(false);
        const root = rootRef.current;
        if (!root) {
            setImagesLoaded(true);
            return;
        }

        const imgs = Array.from(root.querySelectorAll('img')) as HTMLImageElement[];
        if (imgs.length === 0) {
            setImagesLoaded(true);
            return;
        }

        let remaining = imgs.length;
        const listeners: { img: HTMLImageElement; handler: () => void }[] = [];

        const checkDone = () => {
            remaining -= 1;
            if (remaining <= 0) {
                setImagesLoaded(true);
            }
        };

        imgs.forEach((img) => {
            if (img.complete) {
                // already loaded (or errored)
                remaining -= 1;
            } else {
                const handler = () => checkDone();
                img.addEventListener('load', handler);
                img.addEventListener('error', handler);
                listeners.push({ img, handler });
            }
        });

        if (remaining <= 0) {
            setImagesLoaded(true);
        }

        return () => {
            listeners.forEach(({ img, handler }) => {
                img.removeEventListener('load', handler);
                img.removeEventListener('error', handler);
            });
        };
    }, [posts]);

    return (
        <div
            ref={rootRef}
            className={`print-wrap ${imagesLoaded ? 'images-loaded' : ''}`}
            aria-busy={!imagesLoaded}
            style={brandStyle}
        >
            {includeHeader && (() => {
                const resolved = resolveHeaderFields(template, {
                    posts, childName, child, reportDate, startDate, endDate,
                    orgName, authorName, templateName, fieldValues, attendance,
                });
                const columns = template.headerColumns === 2 ? 2 : 1;
                const rows = toHeaderRows(resolved, columns);
                // Logo is org-level; the template only opts out ('none' — for
                // de-identified reports). No org logo → no logo at all: this is
                // the org's document, so it never carries our branding.
                const showLogo = template.headerLogo !== 'none' && !!logoUrl;
                return (
                    <>
                        {showLogo && (
                            <div className="report-logo-div">
                                <img src={logoUrl!} alt={orgName ? `${orgName} logo` : 'Organisation logo'} className="report-logo" />
                            </div>
                        )}

                        {rows.length > 0 && (
                            <table className={`report-table${columns === 2 ? ' report-table--two-col' : ''}`}>
                                <tbody>
                                    {rows.map((row) => (
                                        <tr key={row.map(f => f.key).join('+')}>
                                            {row.map((f) => (
                                                <React.Fragment key={f.key}>
                                                    <th>{f.label}</th>
                                                    {/* A lone field on a two-col row spans the rest */}
                                                    <td colSpan={columns === 2 && row.length === 1 ? 3 : 1}>{f.value}</td>
                                                </React.Fragment>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </>
                );
            })()}

            {postsSection && Object.entries(groupedPosts).map(([date, posts]) => (
                <div key={date} className="date-group">
                    <div className="date-heading">{date}</div>
                    {posts.map((post) => (
                        <div key={post.id} className="post">
                            {/* Published posts render their frozen approved wording
                                (published_text); fall back to text_content for any
                                draft shown via ad-hoc print preview. */}
                            <div className="content">{post.published_text ?? post.text_content}</div>
                            {postsSection.includeImages && (
                                <div className={`images`}>
                                    {/* Reviewer-excluded photos stay on the post but never render in the report */}
                                    {post.uploads?.filter((u) => !u.excluded_from_report).map((u) => (
                                        <img key={u.url} src={useThumbnails && u.thumbnail_url ? u.thumbnail_url : u.url} alt="" />
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            ))}

            {attendanceSection && attendance && (
                <AttendanceSection
                    stats={attendance}
                    showStats={attendanceSection.stats}
                    showCharts={attendanceSection.charts}
                    tiles={attendanceSection.tiles}
                    chartKeys={attendanceSection.chartKeys}
                />
            )}

            {/* Temporarily hidden — restore when ready:
            <div className="report-end-footer">Generated by Rustle It</div> */}

            {/* Floating print button */}
            {imagesLoaded && printWindow && (
                <button className="print-icon-button" onClick={handlePrintClick}>
                    Print
                </button>
            )}
        </div>
    );
}

export default ReportView;